import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';

// Matches the Gastronovi Kassensystem Rabatt dialog: Auswahl (qty scope),
// linked %/€ inputs, computed Gesamt, required Grund, optional Konto.
export default function RabattDialog({ open, onClose, item, onConfirm }) {
  const maxQty = item?.quantity || 1;
  const [quantity, setQuantity] = useState(maxQty);
  const [percent, setPercent] = useState(0);
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [account, setAccount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setQuantity(item?.quantity || 1);
      setPercent(0);
      setAmount(0);
      setReason('');
      setAccount('');
    }
  }, [open, item]);

  const lineTotal = (item?.price || 0) * quantity;
  const computedDiscount = percent > 0 ? lineTotal * (percent / 100) : Math.min(amount, lineTotal);
  const total = Math.max(0, lineTotal - computedDiscount);

  const handleConfirm = async () => {
    if (percent <= 0 && amount <= 0) {
      toast.error('Bitte Prozent oder Betrag angeben');
      return;
    }
    if (reason.trim().length < 3) {
      toast.error('Bitte einen Grund für den Rabatt eingeben');
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm({
        quantity,
        discountPercent: percent,
        discountAmount: amount,
        reason: reason.trim(),
        account: account.trim(),
      });
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Rabatt konnte nicht angewendet werden');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="pos-workflow-dialog max-w-sm mx-4 rounded-none border">
        <DialogHeader><DialogTitle>Rabatt</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          {item ? (
            <div>
              <Label className="text-sm font-medium">Auswahl</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="pos-dialog-secondary flex h-10 w-10 items-center justify-center border"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="pos-dialog-primary flex h-10 flex-1 items-center justify-center font-bold">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                  className="pos-dialog-secondary flex h-10 w-10 items-center justify-center border"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setQuantity(maxQty)}
                  className="pos-dialog-secondary flex h-10 w-10 items-center justify-center border text-xs font-semibold"
                  title="Alle auswählen"
                >
                  A
                </button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">von {maxQty} · {item.product_name}</p>
            </div>
          ) : null}

          <div>
            <Label className="text-sm font-medium">Prozent (%)</Label>
            <Input
              type="number" min={0} max={100} value={percent || ''}
              onChange={(e) => { setPercent(parseFloat(e.target.value) || 0); setAmount(0); }}
              className="pos-dialog-field h-12 rounded-none text-lg"
            />
          </div>
          <div className="text-center text-sm text-muted-foreground">oder</div>
          <div>
            <Label className="text-sm font-medium">Betrag (€)</Label>
            <Input
              type="number" min={0} step={0.5} value={amount || ''}
              onChange={(e) => { setAmount(parseFloat(e.target.value) || 0); setPercent(0); }}
              className="pos-dialog-field h-12 rounded-none text-lg"
            />
          </div>

          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm font-medium">Gesamt</span>
            <span className="text-lg font-bold">€{total.toFixed(2)}</span>
          </div>

          <div>
            <Label className="text-sm font-medium">Grund</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z. B. Entschuldigung, Stammgast"
              className="pos-dialog-field h-11 rounded-none"
            />
          </div>

          <div>
            <Label className="text-sm font-medium">Konto (optional)</Label>
            <Input
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              placeholder="Buchungskonto"
              className="pos-dialog-field h-11 rounded-none"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="pos-dialog-secondary h-12 flex-1 rounded-none border"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="pos-dialog-primary h-12 flex-1 rounded-none"
            >
              Speichern
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
